'use client';

import { useState } from 'react';
import { DollarSign, ShoppingCart, Clock, AlertTriangle, ArrowRight, Users, ClipboardList, FileWarning } from 'lucide-react';
import { NumberCard } from '../../../components/shared/NumberCard';
import { DataTable } from '../../../components/shared/DataTable';
import {
  salesOrders,
  salesInvoices,
  customers,
  getSalesSummary,
  getPendingOrders,
  getOverdueInvoices,
} from '../data/mock-data';
import {
  SALES_ORDER_STATUS_LABELS,
  SALES_ORDER_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_COLORS,
} from '../types';
import type { SalesOrder, SalesOrderStatus, InvoiceStatus } from '../types';
import { formatCurrency } from '../../../types';

import type { SellingPage } from '../index';

type TabType = 'orders' | 'invoices';

interface SellingDashboardProps {
  onNavigate: (page: SellingPage) => void;
}

export function SellingDashboard({ onNavigate }: SellingDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('orders');
  const summary = getSalesSummary();
  const pendingOrders = getPendingOrders();
  const overdueInvoices = getOverdueInvoices();
  const recentOrders = salesOrders.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NumberCard
          title="이번 달 매출"
          value={formatCurrency(summary.totalSales)}
          icon={<DollarSign className="w-4 h-4" />}
          iconClassName="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
          trend={{ value: 12.5, label: '전월 대비' }}
        />
        <NumberCard
          title="총 주문"
          value={summary.totalOrders}
          subtitle={`평균 ${formatCurrency(summary.avgOrderValue)}`}
          icon={<ShoppingCart className="w-4 h-4" />}
          iconClassName="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
        />
        <NumberCard
          title="대기중 주문"
          value={summary.pendingOrders}
          subtitle="처리 필요"
          icon={<Clock className="w-4 h-4" />}
          iconClassName="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
        />
        <NumberCard
          title="미수금"
          value={formatCurrency(summary.outstandingAmount)}
          subtitle={`연체 ${summary.overdueInvoices}건`}
          icon={<AlertTriangle className="w-4 h-4" />}
          iconClassName="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
        />
      </div>

      {/* 고객별 현황 */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">주요 고객</h2>
          <button
            onClick={() => onNavigate('customers')}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            전체 보기 <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {customers.slice(0, 4).map((customer) => (
            <div
              key={customer.id}
              className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => onNavigate('customers')}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{customer.name}</h3>
                  <p className="text-xs text-muted-foreground">{customer.code}</p>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">미수금</span>
                <span className={customer.outstandingAmount > 0 ? 'text-yellow-500' : 'text-green-500'}>
                  {formatCurrency(customer.outstandingAmount)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 탭 형태 테이블 섹션 */}
      <div className="bg-card border border-border rounded-lg">
        {/* 탭 헤더 */}
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'orders'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              최근 주문
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'orders'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>{recentOrders.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'invoices'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <FileWarning className="w-4 h-4" />
              연체 청구서
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'invoices'
                  ? 'bg-red-500 text-white'
                  : 'bg-muted text-muted-foreground'
              }`}>{overdueInvoices.length}</span>
            </button>
          </div>
          <button
            onClick={() => onNavigate(activeTab === 'orders' ? 'orders' : 'invoices')}
            className="text-sm text-primary hover:underline flex items-center gap-1 pr-4"
          >
            전체 보기 <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="p-4">
          {activeTab === 'orders' ? (
            <DataTable
              data={recentOrders}
              columns={[
                { key: 'customerName', label: '고객', width: '30%' },
                {
                  key: 'total',
                  label: '금액',
                  width: '25%',
                  align: 'right' as const,
                  render: (value: unknown) => (
                    <span className="whitespace-nowrap">{formatCurrency(value as number)}</span>
                  ),
                },
                {
                  key: 'status',
                  label: '상태',
                  width: '20%',
                  render: (value: unknown) => {
                    const status = value as SalesOrderStatus;
                    return (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${SALES_ORDER_STATUS_COLORS[status]}`}>
                        {SALES_ORDER_STATUS_LABELS[status]}
                      </span>
                    );
                  },
                },
                {
                  key: 'orderDate',
                  label: '날짜',
                  width: '25%',
                  render: (value: unknown) => (
                    <span className="text-muted-foreground whitespace-nowrap">
                      {value as string}
                    </span>
                  ),
                },
              ]}
              pageSize={5}
              showPagination={false}
            />
          ) : overdueInvoices.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              연체된 청구서가 없습니다
            </p>
          ) : (
            <DataTable
              data={overdueInvoices}
              columns={[
                { key: 'customerName', label: '고객', width: '40%' },
                {
                  key: 'outstandingAmount',
                  label: '미수금',
                  width: '30%',
                  align: 'right' as const,
                  render: (value: unknown) => (
                    <span className="text-red-500 font-medium">
                      {formatCurrency(value as number)}
                    </span>
                  ),
                },
                {
                  key: 'dueDate',
                  label: '만기일',
                  width: '30%',
                  render: (value: unknown) => (
                    <span className="text-red-500 whitespace-nowrap">
                      {new Date(value as string).toLocaleDateString('ko-KR')}
                    </span>
                  ),
                },
              ]}
              pageSize={5}
              showPagination={false}
            />
          )}
        </div>
      </div>

      {/* 빠른 작업 */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="font-semibold mb-4">빠른 작업</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => onNavigate('quotations')}
            className="p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left"
          >
            <div className="text-lg font-semibold mb-1">견적서 작성</div>
            <p className="text-sm text-muted-foreground">새 견적서를 작성합니다</p>
          </button>
          <button
            onClick={() => onNavigate('orders')}
            className="p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left"
          >
            <div className="text-lg font-semibold mb-1">주문 등록</div>
            <p className="text-sm text-muted-foreground">새 판매 주문을 등록합니다</p>
          </button>
          <button
            onClick={() => onNavigate('invoices')}
            className="p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left"
          >
            <div className="text-lg font-semibold mb-1">청구서 발행</div>
            <p className="text-sm text-muted-foreground">청구서를 발행합니다</p>
          </button>
          <button
            onClick={() => onNavigate('customers')}
            className="p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-left"
          >
            <div className="text-lg font-semibold mb-1">고객 추가</div>
            <p className="text-sm text-muted-foreground">새 고객을 등록합니다</p>
          </button>
        </div>
      </div>
    </div>
  );
}
